<template>
  <div class="paid-plan-change-edit-pane">
    <current-plan-card-section
      v-if="currentPlan"
      :sectionTitle="$tc('MypagePage.plan.currentPlan')"
      :plan="currentPlan"
      :contractTerm="contractTerm"
      :isConfirmed="true"
    />

    <choose-other-plan-section
      class="paid-plan-change-edit-pane__choose-plan"
      :selectedPlanGroupId="selectedPlanGroupId"
      :otherPlanList="otherPlanList"
      :futurePlan="futurePlan"
      :contractTerm="contractTerm"
      :scheduledContractTerm="scheduledContractTerm"
      procedureScreen="paidPlanChange"
      @handlerPlanSelect="handlerPlanSelect"
    />

    <comparison-plan-section
      class="paid-plan-change-edit-pane__comparison"
      :section-title="$tc('SignupPage.SelectPlanPage.comparison.comparisonDescriptionForPaid')"
    />

    <action-buttons-section
      class="paid-plan-change-edit-pane__action-buttons"
      scrollTargetSelector=".paid-plan-change-edit-pane"
      :visible="true"
    >
      <atom-input-button
        :linkText="$tc('common.confirm')"
        :isDisabled="disabledConfirmBtn"
        @push="handlerSubmit()"
      />
      <atom-input-button
        :linkText="$tc('common.back')"
        :isDisabled="false"
        colorType="tertiary"
        @push="handlerCancel()"
      />
    </action-buttons-section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import ActionButtonsSection from '@/components/common/form/ActionButtonsSection.vue'
import AtomInputButton from '@/components/atoms/input/AtomInputButton.vue'
import CurrentPlanCardSection from '@/components/SignupPage/common/CurrentPlanCardSection.vue'
import ChooseOtherPlanSection from '@/components/pc/MyPage/PaidPlanChange/PaidPlanChangeEditPane/ChooseOtherPlanSection.vue'
import ComparisonPlanSection from '@/components/SignupPage/common/ComparisonPlanSection.vue'
import ContractPlanDocument, {
  PaidPlanGroupType,
} from '@/store/stores/collectionModule/documents/plan/ContractPlanDocument'
import StoreUtil from '@/store/StoreUtil'
import CouponDocument from '@/store/stores/collectionModule/documents/coupon/CouponDocument'
import useContractTerm from '@/components/hook/useContractTerm'
import { ContractType } from '@/store/stores/collectionModule/documents/organization/OrganizationDocument'

/**
 * プラン変更(有料=>有料) ペイン
 */
export default defineComponent({
  name: 'PaidPlanChangeEditPane',
  components: {
    ActionButtonsSection,
    AtomInputButton,
    CurrentPlanCardSection,
    ChooseOtherPlanSection,
    ComparisonPlanSection,
  },
  props: {
    /**
     * 選択中のプラングループID（プラン）
     */
    selectedPlanGroupId: {
      type: String as PropType<PaidPlanGroupType>,
      required: true,
    },
    /**
     * クーポン情報
     */
    coupon: {
      type: Object as PropType<CouponDocument>,
      default: undefined,
    },
  },
  setup(props, context) {
    const contractInfoStore = StoreUtil.useStore('ContractInfoStore')
    const mypagePageStore = StoreUtil.useStore('MypagePageStore')
    const { effectiveContractPlan, effectiveOrgContract, contractPlans } = contractInfoStore
    const { contractPaidPlans, futureContactInfoPlan, getTargetContractPlan } = mypagePageStore
    const { getCurrentContractTerm, getScheduledContractTerm } = useContractTerm()

    /**
     * 確認ボタン非活性化チェック
     */
    const disabledConfirmBtn = computed(() => props.selectedPlanGroupId === '')

    /**
     * 現在契約中のプラン
     */
    const currentPlan = computed(() =>
      contractPlans.value.find((plan) => plan.planGroupId === effectiveContractPlan?.planGroupId),
    )

    /**
     * 将来のプラン
     */
    const futurePlan = computed(() =>
      futureContactInfoPlan?.value?.contractPlanId
        ? getTargetContractPlan(futureContactInfoPlan?.value?.contractPlanId)
        : null,
    )

    /**
     * 変更可能なプラン
     */
    const otherPlanList = computed(() =>
      contractPaidPlans.value.filter(
        (plan) => plan.planGroupId !== effectiveContractPlan?.planGroupId,
      ),
    )

    /**
     * 契約中のプランの契約期間
     */
    const contractTerm = computed(() =>
      getCurrentContractTerm(
        effectiveContractPlan as ContractPlanDocument,
        effectiveOrgContract as ContractType,
      ),
    )

    /**
     * 契約予定のプランの契約期間
     */
    const scheduledContractTerm = computed(() =>
      futurePlan.value?.planGroupId
        ? getScheduledContractTerm(
            effectiveContractPlan as ContractPlanDocument,
            effectiveOrgContract as ContractType,
            futurePlan.value.planGroupId,
          )
        : null,
    )

    /**
     * プラン選択
     * @param planGroupId {PaidPlanGroupType}
     */
    const handlerPlanSelect = (planGroupId: PaidPlanGroupType) => {
      context.emit('handlerPlanSelect', planGroupId)
    }

    return {
      disabledConfirmBtn,
      currentPlan,
      futurePlan,
      contractTerm,
      scheduledContractTerm,
      otherPlanList,
      handlerPlanSelect,
    }
  },
  methods: {
    /**
     * サブミット処理
     */
    handlerSubmit() {
      this.$emit('handlerSubmit')
    },
    /**
     * キャンセル処理
     */
    handlerCancel() {
      this.$emit('handlerCancel')
    },
  },
})
</script>

<style lang="scss" scoped>
.paid-plan-change-edit-pane {
  width: 100%;
  color: $gray75;
  background: $gray1;

  &__comparison {
    border-top: 1px solid $black10;
    padding-bottom: 240px;
  }

  &__action-buttons {
    bottom: 0;
  }
}
</style>

<style lang="scss">
.paid-plan-change-edit-pane {
  &__action-buttons {
    .action-buttons-section__inner {
      @media screen and (min-width: $responsiveTabletWidth) {
        flex-direction: row-reverse;
      }
    }
  }
}
</style>
